<template>
  <div class="um-root">
    <div class="um-content">

      <!-- ── INVENTORY BAR ── -->
      <div class="um-inv-card">
        <div class="um-section-head">
          <h2 class="um-section-title">Inventory</h2>
          <span class="um-section-subtitle">
            {{ invTotal }} units total &middot; {{ soldAbsorption }}% {{ isMarketStrata ? 'absorbed' : 'leased' }}
          </span>
        </div>
        <div class="um-inv-track">
          <div class="um-inv-sold" :style="{ width: invSoldPct + '%' }"></div>
          <div class="um-inv-active" :style="{ width: invActivePct + '%' }"></div>
          <div v-if="invUnreleasedPct > 0" class="um-inv-unreleased" :style="{ width: invUnreleasedPct + '%' }"></div>
        </div>
        <div class="um-inv-footer">
          <div class="um-inv-legend">
            <div class="um-inv-item">
              <span class="um-inv-swatch" style="background:#FC5868;"></span>
              <span class="um-inv-lbl">Total Units</span>
              <strong class="um-inv-val">{{ invTotal }}</strong>
            </div>
            <div class="um-inv-item">
              <span class="um-inv-swatch" style="background:#FC5868;"></span>
              <span class="um-inv-lbl">Confirmed {{ isMarketStrata ? 'Sold' : 'Leased' }}</span>
              <strong class="um-inv-val">{{ invSold }}</strong>
            </div>
            <div class="um-inv-item">
              <span class="um-inv-swatch" style="background:#343376;"></span>
              <span class="um-inv-lbl">Active</span>
              <strong class="um-inv-val">{{ invActive }}</strong>
            </div>
            <div class="um-inv-item">
              <span class="um-inv-swatch" style="background:#C9CCD2;"></span>
              <span class="um-inv-lbl">Unreleased</span>
              <strong class="um-inv-val">{{ invUnreleased }}</strong>
            </div>
          </div>
          <div class="um-conf-legend">
            <span v-for="(c, k) in confidenceLevels" :key="k" class="um-conf-pill"
              :style="{ background: c.bg + '1A', color: c.bg }">
              <span class="um-conf-dot" :style="{ background: c.bg }"></span>
              {{ c.label }}
            </span>
          </div>
        </div>
        <MlsAvailablePricing :rowData="filteredListings" :isMarketStrata="isMarketStrata" />
      </div>

      <!-- ── FILTER BAR ── -->
      <div class="um-filter-bar">
        <font-awesome-icon :icon="['fas', 'sliders-h']" class="um-filter-icon" />

        <!-- Status switch -->
        <div class="um-filter-switch">
          <button v-for="opt in statusFilterToggles" :key="opt.value" class="um-switch-chip"
            :class="{ 'um-switch-chip--on': filterStatus.includes(opt.value) }"
            @click="toggleArrayFilter('filterStatus', opt.value)">{{ opt.label }}</button>
        </div>

        <div class="um-filter-divider"></div>

        <div class="um-filter-group">
          <treeselect v-model="filterType" :multiple="true" :options="typeOptions" placeholder="Type…"
            class="um-treeselect" />
        </div>
        <div class="um-filter-group">
          <treeselect v-model="filterFpSpecs" :multiple="true" :options="fpSpecsOptions" placeholder="FP Specs…"
            class="um-treeselect" />
        </div>
        <div class="um-filter-group um-filter-group--sm">
          <treeselect v-model="filterPlanName" :multiple="true" :options="planNameOptions" placeholder="Plan Name…"
            class="um-treeselect" />
        </div>
        <div class="um-filter-group um-filter-group--sm">
          <treeselect v-model="filterBaths" :multiple="true" :options="bathsOptions" placeholder="Baths…"
            class="um-treeselect" />
        </div>

        <!-- Price Date slider -->
        <DateSlider label="Price Date" v-model="filterPriceDays" />

        <!-- Sold Date slider -->
        <DateSlider label="Sold Date" v-model="filterSoldDays" />

        <button class="ms-more-filters-btn" :class="{ 'ms-more-filters-btn--active': activeRangeFilterCount > 0 }"
          @click="moreFiltersOpen = true">
          <font-awesome-icon :icon="['fas', 'sliders-h']" />
          More Filters<span v-if="activeRangeFilterCount > 0"> ({{ activeRangeFilterCount }})</span>
        </button>
        <button class="ms-filter-selected-btn" :class="{ 'ms-filter-selected-btn--active': filterToSelected }"
          :disabled="!selectedUids.length && !filterToSelected" @click="toggleFilterSelected">
          <font-awesome-icon :icon="['fas', 'filter']" />
          {{ filterToSelected ? 'Show All' : 'Filter' }}<span v-if="selectedUids.length && !filterToSelected"> ({{
            selectedUids.length }})</span>
        </button>
        <button v-if="activeFieldFilterCount > 0" class="um-filter-clear" @click="clearAllFilters">
          <font-awesome-icon :icon="['fas', 'times']" />
          Clear ({{ activeFieldFilterCount }})
        </button>
      </div>

      <!-- ── FIELDS PANEL ── -->
      <FieldsPanel :shown-cols="fpPanelShownCols" :hidden-cols="fpHiddenCols"
        @update:shownCols="onFieldsPanelShownUpdate" @update:hiddenCols="fpHiddenCols = $event" @reset="onFieldsReset">
        <template #actions>
          <button class="um-save-view-btn mr-2" @click="saveFilters">
            <font-awesome-icon :icon="['fas', savedMsg ? 'check' : 'save']" />
            {{ savedMsg ? 'Saved!' : 'Save View' }}
          </button>
        </template>
      </FieldsPanel>

      <!-- ── LISTINGS PANEL ── -->
      <div class="um-table-layout">
        <div class="um-table-panel">
          <div class="um-table-head">
            <h2 class="um-section-title">Unit Mix</h2>
            <span class="um-section-subtitle" v-if="filteredListings.length">
              {{ filteredListings.length }} of {{ allMlsData.length }} listings
            </span>
            <div class="um-section-actions">
              <button class="um-btn-secondary" @click="exportData">
                <font-awesome-icon :icon="['fas', 'file-export']" />
                Export CSV
              </button>
              <button class="um-btn-secondary" :disabled="loading" @click="generateImage">
                <font-awesome-icon v-if="!loading" :icon="['fas', 'download']" />
                <b-spinner v-else small variant="vb-primary" />
                Download PDF
              </button>
              <button class="um-btn-secondary" @click="onShowUnitPreview(filteredListings)">
                <font-awesome-icon :icon="['fas', 'wave-square']" />
                Floor Plans
              </button>
            </div>
          </div>
          <div class="um-table-wrap">
            <div ref="tabulatorEl"></div>
          </div>
        </div>
      </div>

    </div>

    <!-- ── MORE FILTERS MODAL ── -->
    <transition name="cp-modal">
      <div v-if="moreFiltersOpen" class="cp-overlay" @click.self="moreFiltersOpen = false">
        <div class="mf-dialog">
          <div class="mf-header">
            <div class="cp-header-left">
              <span class="cp-icon"><font-awesome-icon :icon="['fas', 'sliders-h']" /></span>
              <span class="cp-title">More Filters</span>
            </div>
            <button class="cp-close" @click="moreFiltersOpen = false" aria-label="Close">
              <font-awesome-icon :icon="['fas', 'times']" />
            </button>
          </div>
          <div class="mf-body">
            <div v-for="(filter, key) in rangeFilters" :key="key" class="mf-filter-row">
              <div class="mf-filter-label">{{ filter.label }}</div>
              <div class="mf-range">
                <div class="mf-range-col">
                  <span class="mf-input-label">Min</span>
                  <input v-if="filter.type === 'number'" type="number" class="mf-input" placeholder="Min"
                    v-model.number="filter.min" />
                  <input v-else type="date" class="mf-input" v-model="filter.min" />
                </div>
                <span class="mf-sep">—</span>
                <div class="mf-range-col">
                  <span class="mf-input-label">Max</span>
                  <input v-if="filter.type === 'number'" type="number" class="mf-input" placeholder="Max"
                    v-model.number="filter.max" />
                  <input v-else type="date" class="mf-input" v-model="filter.max" />
                </div>
              </div>
            </div>
          </div>
          <div class="mf-footer">
            <button class="mf-reset-btn" @click="clearRangeFilters">
              <font-awesome-icon :icon="['fas', 'undo']" />
              Reset Filters
            </button>
            <button class="mf-apply-btn" @click="moreFiltersOpen = false">
              Apply
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ── LISTING SUMMARY MODAL ── -->
    <transition name="sp-fade">
      <ListingSummaryModal v-if="summaryPopupVisible && summaryPopupRow" :row="summaryPopupRow"
        :history="summaryPopupHistory || []" :is-market-strata="isMarketStrata"
        :raw-data="project" :from-unit-mix="true" :editable="true"
        @close="summaryPopupVisible = false"
        @floor-plan="onFloorPlanFromSummary"
        @editMLSEntryNew="editMLSEntry"
        @deleteMLSEntry="deleteMLSEntry"
        @addMLSEntry="addMLSEntry"
        @entries-saved="onSummaryEntriesSaved" />
    </transition>

    <!-- ── ADD MLS ENTRY MODAL (from Listing Summary) ── -->
    <MlsEntryNewModal modal-id="mls-entry-new-unitmix" :cid="entryFormCid" :is-rental="false"
      @submitted="onEntrySubmitted" />

    <!-- ── FLOOR PLAN IMAGE MODAL ── -->
    <transition name="sp-fade">
      <div v-if="floorPlanPreview" class="sp-overlay" @click.self="floorPlanPreview = null">
        <div class="um-fp-modal">
          <div class="um-fp-modal-header">
            <span class="um-fp-modal-title">Floor Plan</span>
            <button class="um-fp-modal-close" @click="floorPlanPreview = null">✕</button>
          </div>
          <div class="um-fp-modal-body">
            <img :src="floorPlanPreview.preview" class="um-fp-modal-img"
              :title="floorPlanPreview.link ? 'Click to open PDF' : ''"
              :style="floorPlanPreview.link ? 'cursor:pointer' : ''" @click="openFpLink(floorPlanPreview.link)" />
          </div>
        </div>
      </div>
    </transition>

    <!-- ── PLAN PREVIEW MODAL ── -->
    <transition name="sp-fade">
      <div v-if="showFloorPlansModal" class="sp-overlay" @click.self="showFloorPlansModal = false">
        <div class="sp-modal fp-summary-modal">
          <div class="sp-header">
            <h5 class="sp-title">PLAN PREVIEW</h5>
            <span class="sp-header-spacer"></span>
            <img src="/images/assets/logo/RPD_logotype_skyblue.png" class="sp-logo" />
            <button class="sp-close" @click="showFloorPlansModal = false">✕</button>
          </div>
          <div class="um-summary-totals">
            <template v-for="(item, idx) in totalListingSummary">
              <span :key="'sum-' + idx" v-if="item[Object.keys(item)[0]] > 0" class="um-summary-pill">
                {{ Object.keys(item)[0] }}
                <b>{{ item[Object.keys(item)[0]] }}</b>
              </span>
            </template>
          </div>
          <div class="fp-summary-content">
            <SummaryTable v-if="unitPreviewSummary" ref="summaryTableRef" :report-detail-summary="unitPreviewSummary"
              :header-fields="unitPreviewSummaryHeader" :is-market-strata="isMarketStrata"
              v-on:filterSummary="filterSummary" />
          </div>
        </div>
      </div>
    </transition>

    <!-- ── UNIT MIX HISTORY MODAL ── -->
    <b-modal ref="unit-history-modal" title="Unit Mix History" hide-footer>
      <UnitMixReportHistory
        :is-market-strata="isMarketStrata"
        :data="unitMixHistoryData"
        :history="history"
        v-on:set-project-id="addEntry($event)"
        v-on:editMLSEntryNew="editMLSEntry"
        v-on:deleteMLSEntry="deleteMLSEntry"
      />
    </b-modal>

    <!-- ── ADD MLS ENTRY MODAL ── -->
    <b-modal
      id="addMlsEntry"
      title="Add MLS Entry"
      hide-footer
      no-close-on-esc
      no-close-on-backdrop
    >
      <AddMlsForm :cidSelected="cidSelected" :page-name="'unitmix'" />
    </b-modal>

  </div>
</template>

<script>
import { TabulatorFull as Tabulator } from "tabulator-tables";
import { wrapColumns } from "../utilities/tabulatorSorter";
import { BACKEND_URL } from "../variables.js";
import { BModal, BSpinner } from "bootstrap-vue";
import { useUserStore } from "../store/UserStore";
import { mapState } from "pinia";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";

import SummaryTable from "./common/SummaryTable.vue";
import MlsAvailablePricing from "./common/MlsAvailablePricing.vue";
import UnitMixReportHistory from "./common/UnitMixReportHistory.vue";
import AddMlsForm from "./Forms/AddMlsForm.vue";
import ListingSummaryModal from "./modals/ListingSummaryModal.vue";
import MlsEntryNewModal from "./modals/MlsEntryNewModal.vue";
import DateSlider from "./filters/DateSlider.vue";
import FieldsPanel from "./filters/FieldsPanel.vue";

import { saveView, loadView } from "../composables/asyncView.js";

const SKIP_COLS = new Set(["checkbox", "updatemls", "favorite", "custom_price_change"]);
const NET_PRICE_KEYS = new Set(["list_price_net", "net_price", "custom_mls_net_rent"]);
const MONEY_KEYS = new Set([
  "mls_list_price", "mls_sold_price", "list_price_net", "net_price",
  "custom_mls_net_rent", "buyer_incentives", "realtor_incentives",
]);
const PSF_KEYS = new Set(["custom_sold_psf", "custom_psf", "custom_net_psf", "custom_mls_net_psf", "net_sold_psf"]);
const NARROW_KEYS = new Set(["mls_baths", "stalls", "mls_parking", "balcony", "levels"]);
const DATE_KEYS = new Set(["mls_price_date", "mls_list_date", "mls_sold_date"]);

const MONTHS = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };

export default {
  name: "UnitMix",
  components: {
    "b-modal": BModal,
    "b-spinner": BSpinner,
    Treeselect,
    SummaryTable,
    MlsAvailablePricing,
    UnitMixReportHistory,
    AddMlsForm,
    ListingSummaryModal,
    MlsEntryNewModal,
    DateSlider,
    FieldsPanel,
  },

  data() {
    return {
      project: null,
      mergedListingData: [],
      tabulator: null,
      loading: false,

      filterStatus: [],
      filterPriceDays: 0,
      filterSoldDays: 0,
      filterType: [],
      filterFpSpecs: [],
      filterPlanName: [],
      filterBaths: [],
      fpSpecsOptions: [],
      fpHiddenCols: [],
      localRpdList1: null,

      moreFiltersOpen: false,
      rangeFilters: {
        mls_unit_size: { label: "Unit Size (sqft)", type: "number", min: null, max: null },
        mls_list_price: { label: "List Price", type: "number", min: null, max: null },
        custom_sold_psf: { label: "$PSF", type: "number", min: null, max: null },
        list_price_net: { label: "Net Price", type: "number", min: null, max: null },
        custom_net_psf: { label: "Net $PSF", type: "number", min: null, max: null },
        mls_baths: { label: "Baths", type: "number", min: null, max: null },
        mls_list_date: { label: "List Date", type: "date", min: "", max: "" },
        mls_sold_date: { label: "Sold Date", type: "date", min: "", max: "" },
      },

      selectedUids: [],
      filterToSelected: false,
      savedMsg: false,

      showFloorPlansModal: false,
      unitPreviewSummary: null,
      unitPreviewSummaryHeader: null,

      floorPlanPreview: null,
      summaryPopupVisible: false,
      summaryPopupRow: null,
      summaryPopupHistory: null,

      history: null,
      unitMixHistoryData: null,
      cidSelected: 0,
      entryFormCid: null,

      confidenceLevels: {
        verified: { label: "Verified", bg: "#1D5BE0" },
        semi: { label: "Semi-Verified", bg: "#E89244" },
        est: { label: "Estimated", bg: "#D43847" },
      },
    };
  },

  computed: {
    ...mapState(useUserStore, ["current_user"]),

    isAdmin() {
      return (this.current_user?.role || "").toLowerCase() === "admin";
    },

    isMarketStrata() {
      return this.$route.query.is_market_strata == "true";
    },

    getViewCategory() {
      return this.isMarketStrata ? 26 : 27;
    },

    allMlsData() {
      return this.mergedListingData;
    },

    invTotal() { return this.allMlsData.length; },
    invActive() {
      return this.allMlsData.filter(u => this.statusKind(u) === "active").length;
    },
    invSold() {
      return this.allMlsData.filter(u => this.statusKind(u) === "sold").length;
    },
    invUnreleased() {
      return this.allMlsData.filter(u => this.statusKind(u) === "none").length;
    },
    invSoldPct() { return this.invTotal ? Math.round((this.invSold / this.invTotal) * 100) : 0; },
    invActivePct() { return this.invTotal ? Math.round((this.invActive / this.invTotal) * 100) : 0; },
    invUnreleasedPct() { return this.invTotal ? Math.round((this.invUnreleased / this.invTotal) * 100) : 0; },
    soldAbsorption() { return this.invTotal ? Math.round((this.invSold / this.invTotal) * 100) : 0; },

    statusFilterToggles() {
      return [
        { label: "Active", value: "Active" },
        { label: this.isMarketStrata ? "Sold" : "Leased", value: "Sold" },
        { label: "Unreleased", value: "Unreleased" },
      ];
    },

    typeOptions() {
      return [
        { id: "bed_Studio", label: "Studio" },
        { id: "bed_Jr 1-bed", label: "Jr 1-bed" },
        { id: "bed_1-bed", label: "1-bed" },
        { id: "bed_2-bed", label: "2-bed" },
        { id: "bed_3-bed", label: "3-bed" },
        { id: "bed_4-bed", label: "4-bed" },
      ];
    },

    planNameOptions() {
      const seen = new Set();
      return this.allMlsData
        .map(u => u.mls_plan_name)
        .filter(v => v && !seen.has(v) && seen.add(v))
        .sort()
        .map(v => ({ id: v, label: v }));
    },

    bathsOptions() {
      const seen = new Set();
      return this.allMlsData
        .map(u => String(u.mls_baths || ""))
        .filter(v => v && !seen.has(v) && seen.add(v))
        .sort()
        .map(v => ({ id: "bath_" + v, label: v }));
    },

    activeRangeFilterCount() {
      return Object.values(this.rangeFilters).filter(
        (f) =>
          (f.min !== null && f.min !== "") || (f.max !== null && f.max !== "")
      ).length;
    },

    activeFieldFilterCount() {
      return (this.filterStatus.length > 0 ? 1 : 0) +
        (this.filterPriceDays > 0 ? 1 : 0) +
        (this.filterSoldDays > 0 ? 1 : 0) +
        (this.filterType.length > 0 ? 1 : 0) +
        (this.filterFpSpecs.length > 0 ? 1 : 0) +
        (this.filterPlanName.length > 0 ? 1 : 0) +
        (this.filterBaths.length > 0 ? 1 : 0) +
        this.activeRangeFilterCount;
    },

    filteredListings() {
      let rows = this.allMlsData;

      // Non-admins: exclude rows with empty, null, or zero list price.
      // Admins see every row regardless of list price.
      if (!this.isAdmin) {
        rows = rows.filter(u => {
          const raw = u.mls_list_price;
          if (raw === null || raw === undefined || raw === "" || raw === "-") return false;
          return Number(raw) > 0;
        });
      }

      if (this.filterToSelected && this.selectedUids.length) {
        const set = new Set(this.selectedUids);
        rows = rows.filter(u => set.has(u.cid));
      }
      if (this.filterStatus.length > 0) {
        rows = rows.filter(u => {
          const k = this.statusKind(u);
          return (this.filterStatus.includes("Active") && k === "active")
              || (this.filterStatus.includes("Sold") && k === "sold")
              || (this.filterStatus.includes("Unreleased") && k === "none");
        });
      }
      if (this.filterPriceDays > 0) {
        const cutoff = Date.now() - this.filterPriceDays * 86400000;
        rows = rows.filter(u => u.mls_price_date && new Date(u.mls_price_date).getTime() >= cutoff);
      }
      if (this.filterSoldDays > 0) {
        const cutoff = Date.now() - this.filterSoldDays * 86400000;
        rows = rows.filter(u => u.mls_sold_date && new Date(u.mls_sold_date).getTime() >= cutoff);
      }
      if (this.filterType.length > 0) {
        const BED_RAW = { Studio: "s", "Jr 1-bed": "jr" };
        const vals = this.filterType.map(id => {
          const label = id.replace(/^bed_/, "");
          if (BED_RAW[label]) return BED_RAW[label];
          const m = label.match(/^(\d+)-bed$/);
          return m ? m[1] : label;
        });
        rows = rows.filter(u => vals.includes(String(u.mls_beds)));
      }
      if (this.filterFpSpecs.length > 0) {
        rows = rows.filter(u => this.filterFpSpecs.includes(u.fp_specs));
      }
      if (this.filterPlanName.length > 0) {
        rows = rows.filter(u => this.filterPlanName.includes(u.mls_plan_name));
      }
      if (this.filterBaths.length > 0) {
        const vals = this.filterBaths.map(id => id.replace(/^bath_/, ""));
        rows = rows.filter(u => vals.includes(String(u.mls_baths)));
      }

      const activeRange = Object.entries(this.rangeFilters).filter(
        ([, f]) => (f.min !== null && f.min !== "") || (f.max !== null && f.max !== "")
      );
      if (activeRange.length) {
        rows = rows.filter(u => {
          for (const [field, f] of activeRange) {
            const raw = u[field];
            if (raw === null || raw === undefined || raw === "" || raw === "-" || raw === 0 || raw === "0") {
              return false;
            }
            if (f.type === "date") {
              const ts = this.parseRowDate(raw);
              if (ts == null) return false;
              if (f.min && new Date(f.min).getTime() > ts) return false;
              if (f.max && new Date(f.max).getTime() < ts) return false;
            } else {
              const n = Number(raw);
              if (isNaN(n)) return false;
              if (f.min !== null && f.min !== "" && n < Number(f.min)) return false;
              if (f.max !== null && f.max !== "" && n > Number(f.max)) return false;
            }
          }
          return true;
        });
      }

      return rows;
    },

    rpdTableList1() {
      if (this.isAdmin) {
        return [
          { label: "Selected", key: "checkbox", thClass: "align-content-center" },
          { label: "Favorite", key: "favorite", thClass: "align-content-center" },
          { label: "Unit #", key: "mls_address", sortable: true },
          { label: "Plan Name", key: "mls_plan_name", sortable: true },
          { label: "Beds", key: "mls_beds", sortable: true },
          { label: "FP Specs", key: "fp_specs", sortable: true },
          { label: "Baths", key: "mls_baths", sortable: true },
          { label: "Size", key: "mls_unit_size", sortable: true },
          { label: "View", key: "orientation", sortable: true },
          { label: "Balcony", key: "balcony", sortable: true },
          { label: "Balcony Area", key: "balcony_area", sortable: true },
          { label: "Parking", key: "stalls", sortable: true },
          { label: "Stalls", key: "mls_parking", sortable: true },
          { label: "List Date", key: "mls_list_date", sortable: true },
          { label: "List Price", key: "mls_list_price", sortable: true },
          { label: "$PSF", key: "custom_sold_psf", sortable: true },
          { label: "Buyer Incentives", key: "buyer_incentives", sortable: true },
          { label: "Realtor Incentives", key: "realtor_incentives", sortable: true },
          { label: "Net Price", key: "list_price_net", sortable: true },
          { label: "Net $PSF", key: "custom_net_psf", sortable: true },
          { label: "Price Date", key: "mls_price_date", sortable: true },
          { label: "Status", key: "custom_status", sortable: true },
          { label: "Sold Date", key: "mls_sold_date", sortable: true },
          { label: "Sold Price", key: "mls_sold_price", sortable: true },
          { label: "Price Status", key: "price_back_up", sortable: true },
        ];
      }
      return [
        { label: "Selected", key: "checkbox", thClass: "w-small" },
        { label: "Unit #", key: "mls_address", sortable: true },
        { label: "Plan", key: "mls_plan_name", sortable: true },
        { label: "Type", key: "mls_beds", sortable: true },
        { label: "Baths", key: "mls_baths", sortable: true },
        { label: "Unit Size", key: "mls_unit_size", sortable: true },
        { label: "List Price", key: "mls_list_price", sortable: true },
        { label: "$PSF", key: "custom_sold_psf", sortable: true },
        { label: "Incentives", key: "buyer_incentives", sortable: true },
        { label: "Net Price", key: "list_price_net", sortable: true },
        { label: "Net $PSF", key: "custom_net_psf", sortable: true },
        { label: "Price Date", key: "mls_price_date", sortable: true },
        { label: "Status", key: "custom_status", sortable: true },
        { label: "Sold Date", key: "mls_sold_date", sortable: true },
      ];
    },

    effectiveRpdList1() { return this.localRpdList1 || this.rpdTableList1; },

    fpPanelShownCols() {
      return this.effectiveRpdList1.filter(c => !SKIP_COLS.has(c.key));
    },

    totalListingSummary() {
      const total = this.filteredListings.length;
      const active = this.filteredListings.filter(u => this.statusKind(u) === "active").length;
      const sold = this.filteredListings.filter(u => this.statusKind(u) === "sold").length;
      const out = [];
      if (total) out.push({ "Total Units": total });
      if (active) out.push({ "Active Units": active });
      if (sold) out.push({ [this.isMarketStrata ? "Confirmed Sold Units" : "Confirmed Leased Units"]: sold });
      return out;
    },
  },

  watch: {
    filteredListings(newData) {
      if (this.tabulator) this.tabulator.setData(newData);
      else this.$nextTick(() => this.initTabulator());
    },
    selectedUids(val) {
      if (!val.length && this.filterToSelected) this.filterToSelected = false;
    },
    localRpdList1() {
      this.$nextTick(() => {
        if (this.tabulator) {
          this.tabulator.setColumns(this.buildTabulatorColumns());
          this.tabulator.setData(this.filteredListings);
        }
      });
    },
  },

  mounted() {
    this.fetchUnitMix();
    this.fetchFpSpecs();
  },

  beforeDestroy() {
    if (this.tabulator) { this.tabulator.destroy(); this.tabulator = null; }
  },

  methods: {
    statusKind(u) {
      const s = (u.mls_status || u.custom_status || "").toLowerCase();
      if (!s || s === "none") return "none";
      if (s === "sold" || s === "leased") return "sold";
      return "active";
    },

    isRealValue(v) {
      return !["", "-", NaN, 0, "0", null, undefined, "NaN"].includes(v);
    },

    getPsf(row) {
      const list = Number(row.mls_list_price);
      const size = Number(row.mls_unit_size);
      if (!list || !size) return "-";
      return this.isMarketStrata ? Math.round(list / size) : Number(list / size).toFixed(2);
    },

    getNetPsf(netPrice, size) {
      const np = Number(netPrice);
      const sz = Number(size);
      if (!np || !sz) return "-";
      return this.isMarketStrata ? Math.round(np / sz) : Number(np / sz).toFixed(2);
    },

    normalizeRow(row) {
      const netPrice = Number(row.mls_list_price) - Number(row.buyer_incentives || 0);
      const psf = this.getPsf(row);
      const netPsf = this.getNetPsf(netPrice, row.mls_unit_size);
      return {
        ...row,
        rowSelected: false,
        custom_status: row.mls_status || "",
        custom_sold_psf: this.isRealValue(psf) ? psf : "-",
        custom_psf: this.isRealValue(psf) ? psf : "-",
        custom_net_psf: this.isRealValue(netPsf) ? netPsf : "-",
        list_price_net: netPrice > 0 ? netPrice : "-",
      };
    },

    fetchUnitMix() {
      const q = this.$route.query;
      $.get(
        {
          url: `${BACKEND_URL}api/get_unit_mix_group?uid=${q.uid || ""}&unit_key=${q.unit_key || ""}&size_range=${q.size_range || ""}`,
        },
        (data, status) => {
          if (status !== "success" || !data) return;
          this.project = data;
          const active = (data.mls_last_date_active || []).map(r => this.normalizeRow(r));
          const sold = (data.mls_last_date_sold || []).map(r => this.normalizeRow(r));
          this.mergedListingData = [...active, ...sold];
          this.$nextTick(() => {
            this.initTabulator();
            this.loadFilters().then(() => this.initFieldsCols());
          });
        }
      );
    },

    async fetchFpSpecs() {
      try {
        await $.get(`${BACKEND_URL}api/get_fp_specs_tree`, response => {
          this.fpSpecsOptions = (response || [])
            .filter(d => !["*", "None", "Jr Bed", "Jr Bed (2)"].includes(d.label))
            .map(d => ({ id: d.label, label: d.label }));
        });
      } catch (e) {
        console.error("fetchFpSpecs:", e);
      }
    },

    initTabulator() {
      if (!this.$refs.tabulatorEl) return;
      if (this.tabulator) { this.tabulator.destroy(); this.tabulator = null; }
      this.tabulator = new Tabulator(this.$refs.tabulatorEl, {
        data: this.filteredListings,
        layout: "fitData",
        placeholder: "<div class='um-placeholder'>No listings available</div>",
        reactiveData: false,
        rowHeight: 42,
        selectable: true,
        columns: wrapColumns(this.buildTabulatorColumns()),
      });

      const syncSelection = () => {
        this.selectedUids = this.tabulator.getSelectedData().map(r => r.cid).filter(Boolean);
      };
      this.tabulator.on("rowSelected", syncSelection);
      this.tabulator.on("rowDeselected", syncSelection);

      this.tabulator.on("rowClick", (e, row) => {
        if (e.target && e.target.closest && e.target.closest('[tabulator-field="mls_address"]')) return;
        const data = row.getData();
        const cid = data.cid;
        const history = this.project && this.project.mls_data && cid
          ? (this.project.mls_data[cid] || []).map((item, i) => ({ ...item, rowIndex: i }))
          : [];
        this.summaryPopupRow = data;
        this.summaryPopupHistory = history;
        this.summaryPopupVisible = true;
      });
    },

    buildTabulatorColumns() {
      const dash = () => '<span class="um-empty">—</span>';
      const strCell = v => (v == null || v === "" || v === "-") ? dash() : String(v);
      const numCell = (v, fmt) => {
        const n = Number(v);
        return (v == null || v === "" || v === "-" || isNaN(n) || n === 0) ? dash() : fmt(n);
      };
      const money = n => "$" + Math.round(n).toLocaleString("en-CA");
      const decPsf = n => "$" + Number(n).toFixed(2);
      const psfFmt = this.isMarketStrata ? money : decPsf;

      const parseDateValue = (v) => {
        if (v == null || v === "" || v === "-" || v === "1969-12-31") return null;
        if (v instanceof Date) return isNaN(v) ? null : v.getTime();
        const s = String(v).trim();
        const m = s.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2,4})$/);
        if (m) {
          const day = Number(m[1]);
          const mon = MONTHS[m[2].toLowerCase()];
          let year = Number(m[3]);
          if (year < 100) year += 2000;
          if (mon != null && !isNaN(day) && !isNaN(year)) return new Date(year, mon, day).getTime();
        }
        const t = Date.parse(s);
        return isNaN(t) ? null : t;
      };
      const dateSorter = (a, b, _aRow, _bRow, _col, dir) => {
        const ta = parseDateValue(a);
        const tb = parseDateValue(b);
        if (ta == null && tb == null) return 0;
        if (ta == null) return dir === "desc" ? -1 : 1;
        if (tb == null) return dir === "desc" ? 1 : -1;
        return ta - tb;
      };

      const FMT = {
        mls_beds: cell => {
          const raw = (cell.getValue() || "").toString().trim().toLowerCase();
          if (!raw) return dash();
          let label;
          if (raw === "s") label = "Studio";
          else if (raw === "jr") label = "Jr-Bed";
          else label = raw + "-bed";
          const spec = (cell.getRow().getData().fp_specs || "").toString().trim();
          const specHtml = (spec && spec !== "-" && spec.toLowerCase() !== "none")
            ? ` <span style="font-weight:700">(${spec})</span>` : "";
          return `<span style="font-weight:400">${label}</span>${specHtml}`;
        },
        mls_list_price: cell => {
          const v = cell.getValue();
          if (v == null || v === "" || v === "-" || isNaN(Number(v)) || Number(v) === 0) return dash();
          const priceStr = money(Number(v));
          const pb = (cell.getRow().getData().price_back_up || "").toLowerCase();
          const colorMap = { verified: "#1D5BE0", "semi-verified": "#E89244", "educated estimated": "#D43847" };
          const color = colorMap[pb];
          return color
            ? `<span style="color:${color};font-weight:600">${priceStr}</span>`
            : priceStr;
        },
        mls_sold_price: cell => numCell(cell.getValue(), money),
        list_price_net: cell => numCell(cell.getValue(), money),
        net_price: cell => numCell(cell.getValue(), money),
        custom_sold_psf: cell => numCell(cell.getValue(), psfFmt),
        custom_psf: cell => numCell(cell.getValue(), psfFmt),
        custom_net_psf: cell => numCell(cell.getValue(), psfFmt),
        mls_unit_size: cell => numCell(cell.getValue(), n => n.toLocaleString("en-CA")),
        buyer_incentives: cell => numCell(cell.getValue(), money),
        realtor_incentives: cell => numCell(cell.getValue(), money),
        mls_baths: cell => numCell(cell.getValue(), n => String(n)),
        custom_status: cell => {
          const v = (cell.getValue() || "");
          if (!v) return dash();
          const s = v.toLowerCase();
          let cls = "um-status-badge";
          if (s === "active") cls += " um-status-active";
          else if (s === "sold" || s === "leased") cls += " um-status-sold";
          else cls += " um-status-none";
          return `<span class="${cls}">${v}</span>`;
        },
        favorite: cell => {
          const fav = cell.getRow().getData().favor;
          const filled = fav == "1";
          return `<span class="um-fav" style="cursor:pointer;color:${filled ? "#F26A6F" : "#C9CCD2"}">★</span>`;
        },
      };

      return this.effectiveRpdList1
        .filter(col => !SKIP_COLS.has(col.key))
        .map(col => {
          const key = col.key;

          if (key === "checkbox") {
            return {
              title: "",
              formatter: "rowSelection",
              titleFormatter: "rowSelection",
              width: 40,
              minWidth: 40,
              hozAlign: "center",
              headerHozAlign: "center",
              headerSort: false,
              resizable: false,
            };
          }

          if (key === "mls_address") {
            return {
              title: col.label,
              field: key,
              minWidth: 70,
              sorter: "string",
              formatter: cell => {
                const v = cell.getValue();
                if (!v) return dash();
                const fp = cell.getRow().getData().mls_floor_plan_feature;
                if (fp && fp.preview) return `<span class="um-addr-link">${v}</span>`;
                return String(v);
              },
              cellClick: (e, cell) => {
                const fp = cell.getRow().getData().mls_floor_plan_feature;
                if (fp && fp.preview) this.floorPlanPreview = fp;
              },
            };
          }

          if (key === "favorite") {
            return {
              title: col.label,
              field: "favor",
              width: 60,
              minWidth: 60,
              hozAlign: "center",
              headerHozAlign: "center",
              headerSort: false,
              formatter: FMT.favorite,
              cellClick: (e, cell) => this.toggleFavorite(cell.getRow().getData()),
            };
          }

          const isNumeric = MONEY_KEYS.has(key) || PSF_KEYS.has(key) || key === "mls_unit_size" || key === "mls_baths";
          const isNetPrice = NET_PRICE_KEYS.has(key);

          let minWidth = 100;
          if (key === "mls_address" || key === "mls_plan_name") minWidth = 70;
          else if (PSF_KEYS.has(key) || NARROW_KEYS.has(key)) minWidth = 70;
          else if (MONEY_KEYS.has(key)) minWidth = isNetPrice ? 140 : 110;
          else if (key === "mls_unit_size") minWidth = 80;
          else if (key === "custom_status") minWidth = 90;
          else if (DATE_KEYS.has(key)) minWidth = 95;

          const isDate = DATE_KEYS.has(key);
          return {
            title: col.label,
            field: key,
            minWidth,
            sorter: isDate ? dateSorter : (isNumeric ? "number" : "string"),
            hozAlign: isNumeric ? "right" : "left",
            headerHozAlign: isNumeric ? "right" : "left",
            formatter: FMT[key] || (cell => strCell(cell.getValue())),
          };
        });
    },

    toggleArrayFilter(key, val) {
      const idx = this[key].indexOf(val);
      if (idx >= 0) this[key].splice(idx, 1);
      else this[key].push(val);
    },

    toggleFilterSelected() { this.filterToSelected = !this.filterToSelected; },

    parseRowDate(v) {
      if (v == null || v === "" || v === "-" || v === "1969-12-31") return null;
      if (v instanceof Date) return isNaN(v) ? null : v.getTime();
      const s = String(v).trim();
      const m = s.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2,4})$/);
      if (m) {
        const day = Number(m[1]);
        const mon = MONTHS[m[2].toLowerCase()];
        let year = Number(m[3]);
        if (year < 100) year += 2000;
        if (mon != null && !isNaN(day) && !isNaN(year)) return new Date(year, mon, day).getTime();
      }
      const t = Date.parse(s);
      return isNaN(t) ? null : t;
    },

    clearRangeFilters() {
      Object.values(this.rangeFilters).forEach((f) => {
        f.min = f.type === "number" ? null : "";
        f.max = f.type === "number" ? null : "";
      });
    },

    clearAllFilters() {
      this.filterStatus = [];
      this.filterPriceDays = 0;
      this.filterSoldDays = 0;
      this.filterType = [];
      this.filterFpSpecs = [];
      this.filterPlanName = [];
      this.filterBaths = [];
      this.clearRangeFilters();
    },

    openFpLink(link) { if (link) window.open(link, "_blank"); },

    onFloorPlanFromSummary(fp) {
      if (fp && fp.preview) this.floorPlanPreview = fp;
    },

    onFieldsPanelShownUpdate(cols) {
      const fixed = this.effectiveRpdList1.filter(c => SKIP_COLS.has(c.key));
      this.localRpdList1 = [...fixed, ...cols];
    },

    onFieldsReset() {
      this.localRpdList1 = null;
      this.fpHiddenCols = [];
      this.$nextTick(() => this.initFieldsCols());
    },

    initFieldsCols() {
      const all = this.rpdTableList1;
      const shown = this.localRpdList1 || all;
      const shownKeys = new Set(shown.map(c => c.key));
      this.fpHiddenCols = all.filter(c => !shownKeys.has(c.key) && !SKIP_COLS.has(c.key));
    },

    isNotEmpty(response) {
      if (response == null) return false;
      if (typeof response === 'string' && response.trim().length === 0) return false;
      if (Array.isArray(response) && response.length === 0) return false;
      if (typeof response === 'object' && Object.keys(response).length === 0) return false;
      return true;
    },

    async loadFilters() {
      try {
        const res = await loadView(this.getViewCategory, "unit_mix_custom_filter");
        if (this.isNotEmpty(res)) {
          if (Array.isArray(res.filterStatus)) this.filterStatus = res.filterStatus;
          if (typeof res.filterPriceDays === 'number') this.filterPriceDays = res.filterPriceDays;
          if (typeof res.filterSoldDays === 'number') this.filterSoldDays = res.filterSoldDays;
          if (Array.isArray(res.filterType)) this.filterType = res.filterType;
          if (Array.isArray(res.filterFpSpecs)) this.filterFpSpecs = res.filterFpSpecs;
          if (Array.isArray(res.filterPlanName)) this.filterPlanName = res.filterPlanName;
          if (Array.isArray(res.filterBaths)) this.filterBaths = res.filterBaths;
          if (res.rangeFilters && typeof res.rangeFilters === "object") {
            for (const key in res.rangeFilters) {
              if (this.rangeFilters[key]) {
                this.rangeFilters[key].min = res.rangeFilters[key].min;
                this.rangeFilters[key].max = res.rangeFilters[key].max;
              }
            }
          }
        }
      } catch (e) {
        console.log(e);
      }

      try {
        const field_selected = await loadView(this.getViewCategory, "unit_mix_field_selected");
        const custom_headers = await loadView(this.getViewCategory, "unit_mix_custom_headers");
        if (!field_selected) return;
        const all = this.rpdTableList1;
        const loaded = [...(field_selected || []), ...(custom_headers || [])];
        const allKeys = new Set(all.map(c => c.key));
        const isAllPresent = loaded.every(c => allKeys.has(c.key));
        if (isAllPresent && loaded.length === all.length) {
          const keyToStatic = Object.fromEntries(all.map(c => [c.key, c]));
          this.localRpdList1 = field_selected.map(c => keyToStatic[c.key]).filter(Boolean);
          this.fpHiddenCols = (custom_headers || []).map(c => keyToStatic[c.key]).filter(Boolean);
        }
      } catch (e) {
        console.error("loadFilters:", e);
      }
    },

    async saveFilters() {
      const shown = this.effectiveRpdList1.filter(c => !SKIP_COLS.has(c.key));
      try {
        await saveView(this.getViewCategory, "unit_mix_field_selected", { unit_mix_field_selected: shown });
        await saveView(this.getViewCategory, "unit_mix_custom_headers", { unit_mix_custom_headers: this.fpHiddenCols });
        await saveView(this.getViewCategory, "unit_mix_custom_filter", {
          unit_mix_custom_filter: {
            filterStatus: this.filterStatus,
            filterPriceDays: this.filterPriceDays,
            filterSoldDays: this.filterSoldDays,
            filterType: this.filterType,
            filterFpSpecs: this.filterFpSpecs,
            filterPlanName: this.filterPlanName,
            filterBaths: this.filterBaths,
            rangeFilters: JSON.parse(JSON.stringify(this.rangeFilters)),
          },
        });
        this.savedMsg = true;
        setTimeout(() => { this.savedMsg = false; }, 2000);
      } catch (e) {
        console.error("saveFilters:", e);
      }
    },

    onShowUnitPreview(tableData) {
      if (!tableData || !tableData.length) return;
      const vm = this;
      const validNum = v => !["-", "", 0, "0", "$0", null, undefined, NaN].includes(Number(v));

      const grouped = tableData.reduce((acc, row) => {
        const {
          mls_sold_price, mls_plan_name, custom_net_psf: net_psf, mls_status, mls_unit_size,
          fp_specs, mls_beds, mls_baths, cid, mls_floor_plan_feature,
          mls_list_price, list_price_net: net_price, buyer_incentives,
        } = row;
        if (!acc[mls_plan_name]) {
          acc[mls_plan_name] = {
            mls_plan_name, cid: "",
            mls_sold_price: 0, net_price: 0, net_psf: 0,
            units: 0, available_pricing: 0, confirmed_sold: 0,
            total_size: 0, avg_size: 0, type: {}, baths: 0,
            units_percentage: 0, mls_floor_plan_feature: {},
            buyer_incentives: 0, mls_list_price: 0,
          };
        }
        const g = acc[mls_plan_name];
        if (mls_status && mls_status.toLowerCase() === "sold") g.confirmed_sold += 1;
        g.cid = cid;
        g.units += 1;
        g.total_size += Number(mls_unit_size);
        g.avg_size = Math.round(g.total_size / g.units);
        g.baths += Number(mls_baths);
        g.type = { mls_beds, fp_specs };
        g.mls_floor_plan_feature = mls_floor_plan_feature;
        g.units_percentage = tableData.length > 0
          ? Number((g.units / tableData.length) * 100).toFixed(2) : "-";
        if (validNum(mls_list_price)) { g.available_pricing += 1; g.mls_list_price += Number(mls_list_price); }
        if (validNum(net_price)) g.net_price += Number(net_price);
        if (validNum(net_psf)) g.net_psf += Number(net_psf);
        if (validNum(buyer_incentives)) g.buyer_incentives += Number(buyer_incentives);
        if (validNum(mls_sold_price)) g.mls_sold_price += Number(mls_sold_price);
        return acc;
      }, {});

      this.unitPreviewSummary = Object.values(grouped).map(item => {
        const net_price = (item.mls_list_price - item.buyer_incentives) / (item.available_pricing || 1);
        return {
          ...item,
          confirmed_sold: item.confirmed_sold > 0 ? item.confirmed_sold : "-",
          sold_percentage: item.confirmed_sold > 0
            ? `${Math.round(Number((item.confirmed_sold / item.units) * 100))}` : "-",
          units_percentage: Math.round(item.units_percentage),
          rowSelected: false,
          average_net_price: item.mls_list_price > 0 ? Math.round(net_price) : "-",
          average_net_psf: item.net_psf > 0
            ? (vm.isMarketStrata
              ? Math.round(net_price / item.avg_size)
              : Number(net_price / item.avg_size).toFixed(2))
            : "-",
          net_sold_price: item.mls_sold_price > 0 ? Math.round(item.mls_sold_price) : "-",
        };
      });

      this.setUnitPreviewSummaryHeaderFields();
      this.showFloorPlansModal = true;
    },

    setUnitPreviewSummaryHeaderFields() {
      const leasing = !this.isMarketStrata;
      this.unitPreviewSummaryHeader = [
        { label: "", key: "checkbox", thClass: "w-small" },
        { key: "mls_plan_name", label: "Plan Name", tdClass: "border-right-0", sortable: true },
        { key: "type", label: "Type", sortable: true },
        {
          key: "baths", label: "Baths", sortable: true,
          formatter: (value, row, key) => `${Math.round(Number(key.baths / key.units))}`,
        },
        { key: "avg_size", label: "Avg Size", sortable: true },
        { key: "units", label: "Total Units", sortable: true },
        { key: "units_percentage", label: "% of Units", sortable: true },
        { key: "confirmed_sold", label: leasing ? "Conf. Leased" : "Conf. Sold", sortable: true },
        { key: "sold_percentage", label: leasing ? "Leased %" : "Sold %", sortable: true },
        { key: "available_pricing", label: "Avail. Pricing", sortable: true },
        { key: "average_net_price", label: "Net Price", sortable: true },
        { key: "average_net_psf", label: "Net $PSF", sortable: true },
        { key: "view_unit_mix", label: "" },
      ];
    },

    filterSummary(data) {
      this.unitPreviewSummary = data.filter(row => row.rowSelected);
    },

    toggleFavorite(item) {
      const vm = this;
      this.$bvToast.toast(`Updating...`, { title: "Please wait", autoHideDelay: 3000 });
      const endpoint = item.favor == "1" ? "api/mls_unfavor" : "api/mls_favor";
      $.ajax({
        type: "POST",
        url: `${BACKEND_URL}${endpoint}`,
        data: { mls: item.cid },
      }).fail(() => {
        vm.$bvToast.toast(`Something went wrong`, { title: "Oops!", variant: "danger", autoHideDelay: 3000 });
      }).done(() => {
        item.favor = item.favor == "1" ? "0" : "1";
        if (vm.tabulator) vm.tabulator.updateData([item]);
        vm.$bvToast.toast(item.favor == "1" ? "Added to favorites" : "Removed from favorites", {
          title: item.favor == "1" ? "Success!" : "Removed!",
          variant: item.favor == "1" ? "success" : "warning",
          autoHideDelay: 3000,
        });
      });
    },

    addEntry(data) {
      this.cidSelected = data;
      this.$root.$emit("bv::show::modal", "addMlsEntry");
    },

    addMLSEntry({ projectId }) {
      if (!this.isAdmin || !projectId) return;
      this.entryFormCid = projectId;
      this.summaryPopupVisible = false;
      this.$nextTick(() => this.$bvModal.show("mls-entry-new-unitmix"));
    },

    onEntrySubmitted() {
      this.fetchUnitMix();
    },

    // Entries saved from the Listing Summary grid are patched in place instead
    // of refetched, so the table keeps its sort, scroll and selection. The MLS
    // history (and the open popup's copy) take every saved value; the unit's
    // table row mirrors its latest entry (prices, dates, status) and first
    // entry (construction status), with net price / PSF recomputed.
    onSummaryEntriesSaved(saved) {
      if (!Array.isArray(saved) || !saved.length) return;
      const mls = (this.project && this.project.mls_data) || {};
      const asList = (v) => (Array.isArray(v) ? v : (v ? Object.values(v) : []));
      const EMPTY = new Set(["-", "1969-12-31", "31-Dec-69", "01-Jan-70"]);
      const latestKeys = [
        "mls_list_price", "buyer_incentives", "realtor_incentives", "mls_sold_price",
        "mls_sold_date", "mls_price_date", "mls_status", "mls_pid", "price_back_up",
        "levels", "mls_list_date",
      ];
      saved.forEach(({ cid, entry, values }) => {
        [asList(mls[cid]), asList(this.summaryPopupHistory)].forEach((list) => {
          const item = list.find(x => x && x.cid == cid && x.entry == entry)
            || list.find(x => x && x.entry == entry && (x.cid == null || x.cid == cid));
          if (item) Object.assign(item, values);
          ["completion_date", "close_date"].forEach((f) => {
            if (f in values) list.forEach((x) => { if (x && (x.cid == null || x.cid == cid)) x[f] = values[f]; });
          });
        });

        const history = asList(mls[cid]);
        const isLatest = history.length && history[history.length - 1].entry == entry;
        const isFirst = history.length && history[0].entry == entry;
        const upd = {};
        if (isLatest) latestKeys.forEach((k) => { if (k in values) upd[k] = values[k]; });
        if (isFirst && "construction_status" in values) upd.construction_status = values.construction_status;
        if ("completion_date" in values) {
          const c = String(values.completion_date || "").trim();
          upd.construction_completion_date = EMPTY.has(c) ? "" : c;
        }
        if ("close_date" in values) {
          const c = String(values.close_date || "").trim();
          upd.close_date = EMPTY.has(c) ? "" : c;
        }
        if (!Object.keys(upd).length) return;

        const target = this.mergedListingData.find(r => r.cid == cid);
        if (!target) return;
        const next = this.normalizeRow({ ...target, ...upd });
        Object.assign(target, next);
        if (this.summaryPopupRow && this.summaryPopupRow.cid == cid) Object.assign(this.summaryPopupRow, next);
        const row = this.tabulator && this.tabulator.getRows().find(r => r.getData().cid == cid);
        if (row) row.update(next).then(() => row.reformat());
      });
    },

    editMLSEntry(cid) {
      const q = this.$route.query;
      const link = `mls_entry_edit?cid=${cid.projectId}&entry=${cid.projectEntry}&project=${q.project}&fid=${q.fid}&uid=${q.uid}&sid=${q.sid}`;
      window.open(link, "_blank");
    },

    deleteMLSEntry(cid) {
      if (!this.isAdmin) return;
      if (!confirm("Do you really want to delete this MLS Entry?")) return;
      $.ajax({
        type: "POST",
        url: `${BACKEND_URL}api/presale_mls_entry_delete`,
        data: { cid: cid.projectId, entry: cid.projectEntry },
      }).done(() => location.reload());
    },

    exportData() {
      try {
        const headers = this.effectiveRpdList1.filter(h => !SKIP_COLS.has(h.key) && h.key !== "checkbox");
        const rows = this.filteredListings.map(row =>
          headers.map(h => {
            const cell = row[h.key];
            if (["0", 0, undefined, null].includes(cell)) return "-";
            if (typeof cell === "object" && cell !== null) {
              return cell.fp_specs ? cell.type + "/" + cell.fp_specs : (cell.type || "");
            }
            return typeof cell === "string" && cell.includes(",")
              ? `"${cell.replace(/"/g, '""')}"` : cell;
          })
        );
        const csv = [headers.map(h => h.label).join(","), ...rows.map(r => r.join(","))].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "unit-mix.csv");
        link.click();
        URL.revokeObjectURL(url);
        this.$bvToast.toast(`You can now view the downloaded file`, {
          title: "Success!", variant: "success", autoHideDelay: 5000,
        });
      } catch (e) {
        console.error("exportData:", e);
        this.$bvToast.toast(`Something went wrong.`, {
          title: "Oops!", variant: "danger", autoHideDelay: 5000,
        });
      }
    },

    async generateImage() {
      const vm = this;
      vm.loading = true;
      vm.$bvToast.toast(`Download is in progress.`, {
        title: "Downloading...", variant: "info", autoHideDelay: 5000,
      });
      try {
        await this.generateReport();
        vm.$bvToast.toast(`You can now view the downloaded file`, {
          title: "Success!", variant: "success", autoHideDelay: 5000,
        });
      } catch (e) {
        console.error("generateImage:", e);
        vm.$bvToast.toast(`Something went wrong.`, {
          title: "Oops!", variant: "danger", autoHideDelay: 5000,
        });
      } finally {
        vm.loading = false;
      }
    },

    async generateReport() {
      const vm = this;
      const rows = vm.filteredListings;
      const allRows = vm.allMlsData;
      const now = new Date();
      const reportDate = now.toLocaleDateString("en-US", {
        day: "2-digit", month: "short", year: "numeric",
      }).toUpperCase();
      const reportTime = now.toLocaleTimeString("en-US", {
        hour: "2-digit", minute: "2-digit", hour12: false,
      });

      // Column source (mirrors visible Tabulator columns; exclude UI-only fields)
      const NON_DATA = new Set(["checkbox", "favorite", "updatemls"]);
      const dataCols = vm.effectiveRpdList1
        .filter(c => !SKIP_COLS.has(c.key) && !NON_DATA.has(c.key));
      const dataColCount = dataCols.length;
      const isLandscape = dataColCount > 12;
      const pageOpts = isLandscape
        ? { unit: "mm", format: "letter", orientation: "landscape" }
        : { unit: "mm", format: "letter", orientation: "portrait" };

      const doc = new jsPDF(pageOpts);
      const PW = doc.internal.pageSize.width;
      const PH = doc.internal.pageSize.height;

      const C = {
        navy: [18, 40, 63],
        coral: [252, 88, 104],
        violet: [52, 51, 118],
        fg1: [18, 40, 63],
        fg2: [84, 84, 84],
        fg3: [138, 138, 138],
        success: [31, 157, 85],
        successBg: [232, 245, 238],
        danger: [212, 56, 71],
        warning: [217, 119, 6],
        divider: [238, 240, 244],
        borderStrong: [201, 204, 210],
        bgSubtle: [237, 242, 254],
      };
      const M = { l: 14, r: 14 };

      const drawUtilBar = () => {
        doc.setFillColor(...C.navy);
        doc.rect(0, 0, PW, 9, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.setTextColor(255, 255, 255);
        const reportLabel = `REPORT · ${reportDate}`;
        doc.text(reportLabel, M.l, 5.8);
        doc.text(reportTime, M.l + doc.getTextWidth(reportLabel) + 8, 5.8);
        const brandTxt = "REALPROPERTYDATA.CA";
        doc.text(brandTxt, PW - M.r, 5.8, { align: "right" });
        doc.setFillColor(...C.coral);
        doc.circle(PW - M.r - doc.getTextWidth(brandTxt) - 2.5, 4.6, 0.9, "F");
      };

      const drawPageFooter = (pageNum, total) => {
        const fy = PH - 10;
        doc.setDrawColor(...C.divider);
        doc.setLineWidth(0.2);
        doc.line(M.l, fy - 4, PW - M.r, fy - 4);
        doc.setFillColor(...C.coral);
        doc.circle(M.l + 1.2, fy - 1, 0.9, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(6.5);
        doc.setTextColor(...C.fg3);
        doc.text("REAL PROPERTY DATA · CONFIDENTIAL", M.l + 3.5, fy);
        doc.text(`PAGE ${pageNum} OF ${total}`, PW - M.r, fy, { align: "right" });
      };

      // ====== HEADER ======
      let y = 14;

      // Coral accent bar
      doc.setFillColor(...C.coral);
      doc.rect(M.l, y, 10, 1.2, "F");
      y += 4.5;

      // Eyebrow
      const q = vm.$route.query || {};
      const unitKeyLbl = q.unit_key ? String(q.unit_key).toUpperCase() : "";
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      doc.setTextColor(...C.violet);
      doc.text(`UNIT MIX${unitKeyLbl ? " · " + unitKeyLbl : ""}`, M.l, y);

      // Title + legend
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.setTextColor(...C.fg1);
      doc.text("Unit Mix", M.l, y + 7);

      // Legend (right)
      const legend = [
        { dot: C.navy, label: "Verified" },
        { dot: C.coral, label: "Semi-verified" },
        { dot: C.borderStrong, label: "Estimated" },
      ];
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      const legendItemWidths = legend.map(it => doc.getTextWidth(it.label) + 5 + 6);
      const legendTotalW = legendItemWidths.reduce((a, b) => a + b, 0) - 6;
      let lx = PW - M.r - legendTotalW;
      legend.forEach((it, i) => {
        doc.setFillColor(...it.dot);
        doc.circle(lx + 1.2, y + 4.7, 1.1, "F");
        doc.setTextColor(...C.fg2);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7.5);
        doc.text(it.label, lx + 4, y + 6);
        lx += legendItemWidths[i];
      });

      y += 11;
      doc.setDrawColor(...C.divider);
      doc.setLineWidth(0.2);
      doc.line(M.l, y, PW - M.r, y);
      y += 5;

      // ====== SUMMARY STRIP ======
      const soldWord = vm.isMarketStrata ? "sold" : "leased";
      const psfDisp = n => vm.isMarketStrata
        ? "$" + Math.round(n).toLocaleString("en-CA")
        : "$" + Number(n).toFixed(2);
      const money = n => "$" + Math.round(n).toLocaleString("en-CA");
      const moneyCompact = n => {
        if (n == null) return "—";
        if (n >= 1e6) return "$" + (n / 1e6).toFixed(2).replace(/\.?0+$/, "") + "M";
        if (n >= 1e3) return "$" + (n / 1e3).toFixed(0) + "K";
        return "$" + n.toLocaleString("en-US");
      };

      const activeCount = rows.filter(r => vm.statusKind(r) === "active").length;
      const soldCount = rows.filter(r => vm.statusKind(r) === "sold").length;
      const listPsfs = rows
        .map(r => parseFloat(r.custom_sold_psf || r.mls_psf))
        .filter(n => n > 0 && !isNaN(n));
      const avgListPsf = listPsfs.length
        ? listPsfs.reduce((a, b) => a + b, 0) / listPsfs.length : 0;
      const minPsf = listPsfs.length ? Math.min(...listPsfs) : 0;
      const maxPsf = listPsfs.length ? Math.max(...listPsfs) : 0;
      const netPrices = rows
        .map(r => parseFloat(r.list_price_net || r.net_price))
        .filter(n => n > 0 && !isNaN(n));
      const avgNet = netPrices.length
        ? netPrices.reduce((a, b) => a + b, 0) / netPrices.length : 0;

      const sumW = PW - M.l - M.r;
      const sumH = 15;
      doc.setFillColor(...C.bgSubtle);
      doc.roundedRect(M.l, y, sumW, sumH, 2, 2, "F");

      const sumItems = [
        {
          l: "LISTINGS",
          v: String(rows.length || "—"),
          s: `${activeCount} active · ${soldCount} ${soldWord}`,
        },
        {
          l: "AVG LIST $PSF",
          v: avgListPsf ? psfDisp(avgListPsf) : "—",
          s: listPsfs.length ? `Range ${psfDisp(minPsf)} – ${psfDisp(maxPsf)}` : "—",
        },
        {
          l: "AVG NET PRICE",
          v: avgNet ? moneyCompact(avgNet) : "—",
          s: "Net of incentives",
        },
        {
          l: "TOTAL UNITS",
          v: String(allRows.length || "—"),
          s: `${rows.length} shown`,
        },
      ];
      const scW = sumW / 4;
      sumItems.forEach((it, i) => {
        const sx = M.l + i * scW;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(6.2);
        doc.setTextColor(...C.violet);
        doc.text(it.l, sx + 4, y + 4.5);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.setTextColor(...C.fg1);
        doc.text(it.v, sx + 4, y + 10);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(6);
        doc.setTextColor(...C.fg3);
        doc.text(it.s, sx + 4, y + 13);
      });
      y += sumH + 5;

      // ====== LISTING TABLE ======
      // Money-style columns (cash amounts, not PSF)
      const CASH_KEYS = new Set([
        "mls_list_price", "mls_sold_price", "buyer_incentives", "realtor_incentives",
      ]);
      const NUMERIC_RIGHT = new Set([
        ...CASH_KEYS, ...NET_PRICE_KEYS, ...PSF_KEYS, "mls_unit_size", "mls_baths",
      ]);

      const widthHint = (k) => {
        if (NET_PRICE_KEYS.has(k)) return 26;
        if (CASH_KEYS.has(k)) return 20;
        if (k === "mls_beds") return 22;
        if (k === "mls_address" || k === "mls_plan_name") return 16;
        if (k === "mls_unit_size") return 14;
        if (NARROW_KEYS.has(k)) return 10;
        if (k === "custom_status") return 16;
        if (DATE_KEYS.has(k)) return 16;
        if (PSF_KEYS.has(k)) return 12;
        if (k === "orientation" || k === "balcony_area" || k === "fp_specs") return 12;
        if (k === "price_back_up") return 16;
        return 13;
      };

      const verifyColW = 2.2;
      const availW = PW - M.l - M.r - verifyColW;
      const rawWidths = dataCols.map(c => widthHint(c.key));
      const rawTotal = rawWidths.reduce((a, b) => a + b, 0);
      const scale = availW / rawTotal;
      const colWidths = rawWidths.map(w => w * scale);

      const decPsf = n => "$" + Number(n).toFixed(2);
      const psfFmt = vm.isMarketStrata ? money : decPsf;
      const fmtCell = (row, key) => {
        const v = row[key];
        if (key === "mls_beds") {
          const raw = (v || "").toString().toLowerCase();
          const label = raw === "s" ? "Studio"
            : raw === "jr" ? "Jr-Bed"
              : raw ? raw + "-bed" : "—";
          const spec = (row.fp_specs || "").toString().trim();
          return spec && spec.toLowerCase() !== "none" && spec !== "-"
            ? `${label} / ${spec}` : label;
        }
        if (NET_PRICE_KEYS.has(key) || CASH_KEYS.has(key)) {
          const n = Number(v);
          return (v == null || v === "" || v === "-" || !n || isNaN(n)) ? "—" : money(n);
        }
        if (PSF_KEYS.has(key)) {
          const n = Number(v);
          return (v == null || v === "" || v === "-" || !n || isNaN(n)) ? "—" : psfFmt(n);
        }
        if (key === "mls_unit_size") {
          const n = Number(v);
          return (v == null || v === "" || v === "-" || !n || isNaN(n))
            ? "—" : n.toLocaleString("en-CA");
        }
        if (v == null || v === "" || v === "-") return "—";
        if (typeof v === "object" && v !== null) {
          return v.fp_specs ? `${v.type} / ${v.fp_specs}` : (v.type || "—");
        }
        return String(v);
      };

      const rowMeta = rows.map(row => {
        const verify = (row.price_back_up || "").toLowerCase();
        const verifyKey = verify === "verified" ? "v"
          : verify === "semi-verified" ? "sv" : "es";
        const statusStr = (row.custom_status || row.mls_status || "").toLowerCase();
        return {
          verifyKey,
          isActive: /active/.test(statusStr),
          isSold: /sold|leased/.test(statusStr),
          statusRaw: row.custom_status || row.mls_status || "",
          priceBackUp: verify,
          planName: row.mls_plan_name && row.mls_plan_name !== "-" ? row.mls_plan_name : "",
        };
      });

      const body = rows.map(row => {
        const cells = [""]; // verify stripe placeholder
        dataCols.forEach(c => cells.push(fmtCell(row, c.key)));
        return cells;
      });

      const headRow = ["", ...dataCols.map(c => c.label)];
      const columnStyles = { 0: { cellWidth: verifyColW, halign: "left" } };
      dataCols.forEach((c, i) => {
        columnStyles[i + 1] = {
          cellWidth: colWidths[i],
          halign: NUMERIC_RIGHT.has(c.key) ? "right" : "left",
        };
      });

      const colKeyAt = (idx) => idx === 0 ? "__verify" : dataCols[idx - 1]?.key;
      const baseFontSize = isLandscape
        ? (dataColCount > 20 ? 5.6 : 6.2)
        : 6.8;
      const headFontSize = baseFontSize - 0.6;

      autoTable(doc, {
        startY: y,
        margin: { left: M.l, right: M.r, top: 14, bottom: 18 },
        head: [headRow],
        body,
        theme: "plain",
        styles: {
          font: "helvetica",
          fontSize: baseFontSize,
          cellPadding: { top: 1.8, right: 1.2, bottom: 1.8, left: 1.2 },
          overflow: "ellipsize",
          textColor: C.fg1,
        },
        headStyles: {
          fontStyle: "bold", fontSize: headFontSize, textColor: C.fg2,
          overflow: "linebreak", valign: "bottom",
          cellPadding: { top: 1.5, right: 1.2, bottom: 2, left: 1.2 },
        },
        bodyStyles: { valign: "middle" },
        columnStyles,
        didParseCell: (data) => {
          if (data.section === "head") return;
          const colKey = colKeyAt(data.column.index);
          const m = rowMeta[data.row.index];
          if (!m) return;

          if (colKey === "mls_address") {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fontSize = baseFontSize + 0.4;
          }
          if (colKey === "mls_list_price") {
            const colorMap = {
              "verified": [29, 91, 224],
              "semi-verified": [232, 146, 68],
              "educated estimated": [212, 56, 71],
            };
            const txt = data.cell.text[0];
            if (txt && txt !== "—") {
              const cc = colorMap[m.priceBackUp];
              if (cc) data.cell.styles.textColor = cc;
              data.cell.styles.fontStyle = "bold";
            }
          }
          if (NET_PRICE_KEYS.has(colKey) || CASH_KEYS.has(colKey)) {
            data.cell.styles.fontStyle = "bold";
          }
          if (data.cell.text && data.cell.text[0] === "—") {
            data.cell.styles.textColor = [199, 201, 206];
            data.cell.styles.fontStyle = "normal";
          }
          if (colKey === "__verify"
            || colKey === "mls_plan_name"
            || colKey === "custom_status"
            || colKey === "price_back_up") {
            data.cell.text = [""];
          }
        },
        didDrawCell: (data) => {
          if (data.section === "head") {
            doc.setDrawColor(...C.navy);
            doc.setLineWidth(0.4);
            doc.line(data.cell.x, data.cell.y + data.cell.height,
              data.cell.x + data.cell.width, data.cell.y + data.cell.height);
            return;
          }
          doc.setDrawColor(241, 242, 245);
          doc.setLineWidth(0.15);
          doc.line(data.cell.x, data.cell.y + data.cell.height,
            data.cell.x + data.cell.width, data.cell.y + data.cell.height);

          const colKey = colKeyAt(data.column.index);
          const m = rowMeta[data.row.index];
          if (!m) return;

          // Verification stripe
          if (colKey === "__verify") {
            const colorMap = { v: C.navy, sv: C.coral, es: C.borderStrong };
            doc.setFillColor(...(colorMap[m.verifyKey] || C.borderStrong));
            doc.rect(data.cell.x + 0.4, data.cell.y + 1, 1, data.cell.height - 2, "F");
            return;
          }

          // Plan chip
          if (colKey === "mls_plan_name" && m.planName) {
            doc.setFont("helvetica", "bold");
            doc.setFontSize(baseFontSize - 1);
            const tw = doc.getTextWidth(m.planName);
            const chipX = data.cell.x + 1.2;
            const chipY = data.cell.y + (data.cell.height - 3.2) / 2;
            doc.setFillColor(...C.bgSubtle);
            doc.roundedRect(chipX, chipY, tw + 2.4, 3.2, 0.7, 0.7, "F");
            doc.setTextColor(...C.violet);
            doc.text(m.planName, chipX + 1.2, chipY + 2.3);
            return;
          }

          // Status pill
          if (colKey === "custom_status" && m.statusRaw) {
            const soldLbl = vm.isMarketStrata ? "SOLD" : "LEASED";
            const lbl = m.isActive ? "ACTIVE"
              : m.isSold ? soldLbl
                : m.statusRaw.toUpperCase();
            const fg = m.isActive ? C.success : (m.isSold ? C.fg2 : C.fg2);
            const dotC = m.isActive ? C.success : C.fg3;
            doc.setFillColor(...dotC);
            doc.circle(data.cell.x + 1.7, data.cell.y + data.cell.height / 2, 0.7, "F");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(baseFontSize - 0.4);
            doc.setTextColor(...fg);
            doc.text(lbl, data.cell.x + 3.1, data.cell.y + data.cell.height / 2 + 0.8);
            return;
          }

          // Price-status badge
          if (colKey === "price_back_up" && m.priceBackUp) {
            const colorMap = {
              "verified": C.navy,
              "semi-verified": C.coral,
              "educated estimated": C.danger,
            };
            const lblMap = {
              "verified": "VERIFIED",
              "semi-verified": "SEMI",
              "educated estimated": "ESTIMATED",
            };
            const fg = colorMap[m.priceBackUp] || C.fg3;
            const lbl = lblMap[m.priceBackUp] || m.priceBackUp.toUpperCase();
            doc.setFillColor(...fg);
            doc.circle(data.cell.x + 1.7, data.cell.y + data.cell.height / 2, 0.7, "F");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(baseFontSize - 0.6);
            doc.setTextColor(...fg);
            doc.text(lbl, data.cell.x + 3.1, data.cell.y + data.cell.height / 2 + 0.8);
            return;
          }
        },
      });

      // ---- Util bar + footer on every page ----
      const total = doc.internal.getNumberOfPages();
      for (let i = 1; i <= total; i++) {
        doc.setPage(i);
        drawUtilBar();
        drawPageFooter(i, total);
      }

      window.open(
        doc.output("bloburl"),
        "_blank",
        "toolbar=no,status=no,menubar=no,scrollbars=no,resizable=no,modal=yes,top=100,left=500,width=1000,height=800",
      );
    },
  },
};
</script>

<style scoped>
/* ── Root ── */
.um-root {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 100vh;
  overflow-y: auto;
  background: var(--rpd-bg, #f6f8fb);
}

.um-content {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

/* ── Section heads ── */
.um-section-head {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.um-section-title {
  margin: 0;
  font-family: Poppins, sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #12283F;
}

.um-section-subtitle {
  font-size: 12px;
  color: #7E8B97;
}

.um-section-actions {
  margin-left: auto;
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

/* ── Buttons ── */
.um-btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  background: #fff;
  color: #12283F;
  border: 1px solid #E9E8E9;
  border-radius: 8px;
  font-family: Poppins, sans-serif;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
}

.um-btn-secondary:hover {
  border-color: #C9CCD2;
}

.um-btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ── Inventory card ── */
.um-inv-card {
  background: #fff;
  border: 1px solid #E9E8E9;
  border-radius: 12px;
  padding: 18px 20px 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow: hidden;
}

.um-inv-track {
  height: 6px;
  border-radius: 99px;
  background: #EDF2FE;
  display: flex;
  overflow: hidden;
}

.um-inv-sold { background: #FC5868; height: 100%; }
.um-inv-active { background: #343376; height: 100%; border-left: 2px solid #fff; }
.um-inv-unreleased { background: #C9CCD2; height: 100%; border-left: 2px solid #fff; }

.um-inv-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.um-inv-legend {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.um-inv-item {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
}

.um-inv-swatch {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex-shrink: 0;
}

.um-inv-lbl { color: #7E8B97; }
.um-inv-val { color: #12283F; font-weight: 700; }

.um-conf-legend {
  display: flex;
  gap: 6px;
  align-items: center;
  flex-wrap: wrap;
}

.um-conf-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 7px 2px 6px;
  border-radius: 99px;
  font-size: 10px;
  font-weight: 600;
}

.um-conf-dot {
  width: 5px;
  height: 5px;
  border-radius: 99px;
  flex-shrink: 0;
}

/* ── Filter bar ── */
.um-filter-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #fff;
  border: 1px solid #E9E8E9;
  border-radius: 12px;
  flex-wrap: wrap;
}

.um-filter-icon {
  color: #9ca3af;
  font-size: 13px;
  flex-shrink: 0;
}

.um-filter-group {
  flex: 1;
  min-width: 140px;
  max-width: 220px;
}

.um-filter-group--sm {
  flex: 0 0 auto;
  width: 110px;
  min-width: 0;
}

.um-filter-divider {
  width: 1px;
  height: 24px;
  background: #e2e5ea;
  flex-shrink: 0;
}

.um-filter-switch {
  display: inline-flex;
  align-items: center;
  border: 1px solid #e2e5ea;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
}

.um-switch-chip {
  height: 32px;
  padding: 0 11px;
  background: #fff;
  border: none;
  border-right: 1px solid #e2e5ea;
  font-family: Poppins, sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  white-space: nowrap;
}

.um-switch-chip:last-child { border-right: none; }
.um-switch-chip:hover { background: #f3f4f6; color: #12283F; }
.um-switch-chip--on { background: #12283F; color: #fff; }
.um-switch-chip--on:hover { background: #1a3550; color: #fff; }

.ms-filter-selected-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #d97706;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #d97706;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
  flex-shrink: 0;
}

.ms-filter-selected-btn:hover:not(:disabled) { background: #fef3c7; }
.ms-filter-selected-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.ms-filter-selected-btn--active { background: #d97706; color: #fff; border-color: #d97706; }
.ms-filter-selected-btn--active:hover:not(:disabled) { background: #b45309; border-color: #b45309; }

.ms-more-filters-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #e2e4ea;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  color: #545454;
  background: #fff;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
  transition: all 120ms cubic-bezier(0.2, 0, 0, 1);
  flex-shrink: 0;
}

.ms-more-filters-btn:hover {
  background: #f7f8fb;
  border-color: #c9ccd2;
  color: #12283f;
}

.ms-more-filters-btn--active {
  background: #12283f;
  color: #ffffff;
  border-color: #12283f;
}

.ms-more-filters-btn--active:hover {
  background: #1a3554;
  border-color: #1a3554;
}

.um-filter-clear {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #D43847;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  color: #D43847;
  background: transparent;
  cursor: pointer;
  font-family: Poppins, sans-serif;
  white-space: nowrap;
  flex-shrink: 0;
}

.um-filter-clear:hover { background: #fff0f1; }

.um-save-view-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #15803d;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #15803d;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
}

.um-save-view-btn:hover { background: #f0fdf4; }

/* ── Table layout ── */
.um-table-layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
  align-items: start;
}

.um-table-panel {
  background: #fff;
  border: 1px solid #E9E8E9;
  border-radius: 12px;
  overflow: hidden;
}

.um-table-head {
  padding: 14px 18px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  border-bottom: 1px solid #EEF0F4;
  background: #F7F8FB;
}

.um-table-wrap { overflow: hidden; }

/* ── Floor-plan modal ── */
.um-fp-modal {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  max-width: 560px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.um-fp-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #F7F8FB;
  border-bottom: 1px solid #EEF0F4;
}

.um-fp-modal-title {
  font-family: Poppins, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #12283F;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.um-fp-modal-close {
  background: none;
  border: none;
  font-size: 15px;
  color: #7E8B97;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.um-fp-modal-body {
  overflow-y: auto;
  padding: 16px;
  display: flex;
  justify-content: center;
}

.um-fp-modal-img {
  width: 100%;
  display: block;
  border-radius: 6px;
}

/* ── Shared modal styles ── */
.sp-overlay {
  position: fixed;
  inset: 0;
  background: rgba(18, 40, 63, 0.55);
  z-index: 1000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 16px;
  overflow-y: auto;
}

.sp-modal {
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 860px;
  overflow: hidden;
}

.sp-header {
  display: flex;
  align-items: center;
  padding: 18px 22px;
  border-bottom: 1px solid #E9E8E9;
  gap: 12px;
}

.sp-title {
  margin: 0;
  font-family: Poppins, sans-serif;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #12283F;
}

.sp-logo { height: 18px; }
.sp-header-spacer { flex: 1; }

.sp-close {
  margin-left: auto;
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  color: #7E8B97;
  padding: 4px 8px;
  border-radius: 6px;
}

.sp-close:hover { background: #F4F6FA; }

.fp-summary-modal { max-width: 1100px; }
.fp-summary-content {
  padding: 16px 0;
  overflow-x: auto;
}

.um-summary-totals {
  display: flex;
  gap: 8px;
  padding: 12px 22px 0;
  flex-wrap: wrap;
}

.um-summary-pill {
  background: #EDF2FE;
  color: #343376;
  padding: 4px 10px;
  border-radius: 99px;
  font-size: 11px;
  font-weight: 500;
}

.um-summary-pill b { font-weight: 700; margin-left: 4px; }

.sp-fade-enter-active,
.sp-fade-leave-active { transition: opacity 0.2s; }

.sp-fade-enter,
.sp-fade-leave-to { opacity: 0; }

/* ── Responsive ── */
@media (max-width: 1024px) {
  .um-table-layout { grid-template-columns: 1fr; }
}

@media (max-width: 640px) {
  .um-content { padding: 14px 14px 80px; gap: 14px; }
  .um-filter-bar { padding: 8px 10px; }
  .um-filter-group, .um-filter-group--sm { max-width: 100%; width: 100%; }
}

/* ── Dark mode ── */
html.dark .um-root { background: #0E1B2C; }
html.dark .um-content { background: #0e1b2c; }
html.dark .um-section-title { color: #d6e4f0; }
html.dark .um-section-subtitle { color: rgba(214, 228, 240, 0.6); }
html.dark .um-btn-secondary {
  background: #1d3048;
  color: #d6e4f0;
  border-color: rgba(255, 255, 255, 0.08);
}
html.dark .um-btn-secondary:hover {
  border-color: rgba(255, 255, 255, 0.18);
  background: #243d57;
}

html.dark .um-inv-card {
  background: #152030;
  border-color: rgba(255, 255, 255, 0.08);
}
html.dark .um-inv-track { background: rgba(255, 255, 255, 0.08); }
html.dark .um-inv-active { border-left-color: #152030; }
html.dark .um-inv-unreleased {
  background: rgba(255, 255, 255, 0.18);
  border-left-color: #152030;
}
html.dark .um-inv-lbl { color: rgba(214, 228, 240, 0.6); }
html.dark .um-inv-val { color: #d6e4f0; }

html.dark .um-table-panel {
  background: #152030;
  border-color: rgba(255, 255, 255, 0.08);
}
html.dark .um-table-head {
  background: #0e1b2c;
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

html.dark .um-filter-bar {
  background: #152030;
  border-color: rgba(255, 255, 255, 0.08);
}
html.dark .um-filter-icon { color: rgba(214, 228, 240, 0.5); }
html.dark .um-filter-divider { background: rgba(255, 255, 255, 0.08); }
html.dark .um-filter-switch { border-color: rgba(255, 255, 255, 0.08); }
html.dark .um-switch-chip {
  background: #152030;
  border-right-color: rgba(255, 255, 255, 0.08);
  color: rgba(214, 228, 240, 0.6);
}
html.dark .um-switch-chip:hover { background: #1d3048; color: #d6e4f0; }
html.dark .um-switch-chip--on { background: #d6e4f0; color: #12283F; }

html.dark .ms-filter-selected-btn { color: #fbbf24; border-color: #d97706; }
html.dark .ms-filter-selected-btn:hover:not(:disabled) { background: rgba(217, 119, 6, 0.18); }
html.dark .ms-filter-selected-btn--active {
  background: #d97706;
  color: #fff;
  border-color: #d97706;
}

html.dark .um-filter-clear { border-color: #f87171; color: #f87171; }
html.dark .um-filter-clear:hover { background: rgba(248, 113, 113, 0.18); }

html.dark .um-save-view-btn { border-color: #4ade80; color: #4ade80; }
html.dark .um-save-view-btn:hover { background: rgba(74, 222, 128, 0.18); }

html.dark .um-fp-modal {
  background: #152030;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}
html.dark .um-fp-modal-header {
  background: #0e1b2c;
  border-bottom-color: rgba(255, 255, 255, 0.08);
}
html.dark .um-fp-modal-title { color: #d6e4f0; }
html.dark .um-fp-modal-close { color: rgba(214, 228, 240, 0.6); }

html.dark .sp-overlay { background: rgba(0, 0, 0, 0.65); }
html.dark .sp-modal {
  background: #152030;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}
html.dark .sp-header { border-bottom-color: rgba(255, 255, 255, 0.08); }
html.dark .sp-title { color: #d6e4f0; }
html.dark .sp-close { color: rgba(214, 228, 240, 0.6); }
html.dark .sp-close:hover { background: #1d3048; color: #d6e4f0; }
html.dark .um-summary-pill {
  background: rgba(165, 168, 232, 0.18);
  color: #a5a8e8;
}
html.dark .um-conf-pill { opacity: 0.9; }

/* ── More Filters modal ── */
.cp-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(3px);
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.cp-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.cp-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #0e1b2c;
  border-radius: 8px;
  color: #ffffff;
  font-size: 14px;
}

.cp-title {
  font-size: 17px;
  font-weight: 600;
  color: #111827;
  letter-spacing: -0.01em;
}

.cp-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: #f3f4f6;
  border-radius: 8px;
  color: #6b7280;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.15s, color 0.15s;
}

.cp-close:hover {
  background: #e5e7eb;
  color: #111827;
}

.mf-dialog {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.18), 0 4px 16px rgba(0, 0, 0, 0.08);
  width: 100%;
  max-width: 560px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.mf-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.mf-body {
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  max-height: 60vh;
}

.mf-filter-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.mf-filter-label {
  width: 190px;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
}

.mf-range {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex: 1;
}

.mf-range-col {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.mf-input-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #9ca3af;
}

.mf-input {
  height: 34px;
  padding: 0 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  color: #111827;
  background: #fafafa;
  outline: none;
  width: 100%;
  font-family: inherit;
  transition: border-color 0.15s, background 0.15s;
}

.mf-input:focus {
  border-color: #0e1b2c;
  background: #fff;
}

.mf-sep {
  font-size: 13px;
  color: #d1d5db;
  padding-bottom: 6px;
  flex-shrink: 0;
}

.mf-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px 20px;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.mf-reset-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s, border-color 0.15s;
}

.mf-reset-btn:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
  color: #374151;
}

.mf-apply-btn {
  height: 34px;
  padding: 0 20px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  background: #0e1b2c;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s;
}

.mf-apply-btn:hover {
  background: #1a2e47;
}

/* ── More Filters modal transition ── */
.cp-modal-enter-active,
.cp-modal-leave-active {
  transition: opacity 0.2s ease;
}

.cp-modal-enter-active .mf-dialog,
.cp-modal-leave-active .mf-dialog {
  transition: transform 0.22s cubic-bezier(0.34, 1.26, 0.64, 1),
    opacity 0.2s ease;
}

.cp-modal-enter,
.cp-modal-leave-to {
  opacity: 0;
}

.cp-modal-enter .mf-dialog {
  transform: scale(0.94) translateY(12px);
  opacity: 0;
}

.cp-modal-leave-to .mf-dialog {
  transform: scale(0.96) translateY(8px);
  opacity: 0;
}

/* Dark mode — More Filters modal */
html.dark .mf-dialog {
  background: #0e1b2c;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5);
}

html.dark .mf-header {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

html.dark .cp-title {
  color: #f9fafb;
}

html.dark .cp-close {
  background: rgba(255, 255, 255, 0.07);
  color: rgba(255, 255, 255, 0.5);
}

html.dark .cp-close:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}

html.dark .mf-filter-label {
  color: rgba(255, 255, 255, 0.75);
}

html.dark .mf-input {
  background: #152030;
  border-color: rgba(255, 255, 255, 0.1);
  color: #d6e4f0;
}

html.dark .mf-input:focus {
  border-color: #4db7fe;
  background: #1a2a3f;
}

html.dark .mf-input::-webkit-calendar-picker-indicator {
  filter: invert(1) opacity(0.5);
}

html.dark .mf-sep {
  color: rgba(255, 255, 255, 0.2);
}

html.dark .mf-footer {
  border-top-color: rgba(255, 255, 255, 0.08);
}

html.dark .mf-reset-btn {
  border-color: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.5);
}

html.dark .mf-reset-btn:hover {
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.8);
}

html.dark .mf-apply-btn {
  background: #4db7fe;
}

html.dark .mf-apply-btn:hover {
  background: #3aa8f0;
}

html.dark .ms-more-filters-btn {
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  border-color: rgba(255, 255, 255, 0.2);
}

html.dark .ms-more-filters-btn:hover {
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(255, 255, 255, 0.4);
  color: #ffffff;
}

html.dark .ms-more-filters-btn--active {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.25);
  color: #ffffff;
}
</style>

<style>
/* ── Tabulator overrides (unscoped — Tabulator owns its DOM) ── */
.um-table-wrap .tabulator {
  border: none !important;
  background: #ffffff !important;
  font-family: Poppins, -apple-system, sans-serif !important;
  font-size: 11px !important;
}

.um-table-wrap .tabulator-header {
  background: #F7F8FB !important;
  border-bottom: 1px solid #E9E8E9 !important;
  border-top: none !important;
  position: sticky;
  top: 0;
  z-index: 10;
}

.um-table-wrap .tabulator-col {
  background: transparent !important;
  border-right: none !important;
  padding: 0 !important;
}

.um-table-wrap .tabulator-col-content { padding: 10px 14px !important; }

.um-table-wrap .tabulator-col-title {
  font-size: 10px !important;
  font-weight: 600 !important;
  color: #7E8B97 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.08em !important;
  white-space: nowrap !important;
}

.um-table-wrap .tabulator-col.tabulator-sortable:hover .tabulator-col-title {
  color: #12283F !important;
}

.um-table-wrap .tabulator-col.tabulator-col-sorter-element:hover {
  background: #EDF2FE !important;
}

.um-table-wrap .tabulator-col .tabulator-col-sorter { color: #C9CCD2 !important; }
.um-table-wrap .tabulator-col.tabulator-col-sorter-element .tabulator-col-sorter { color: #12283F !important; }

.um-table-wrap .tabulator-tableHolder { background: #ffffff !important; }

.um-table-wrap .tabulator-row {
  background: #ffffff !important;
  border-bottom: 1px solid #EEF0F4 !important;
  transition: background 0.1s !important;
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: nowrap !important;
  align-items: stretch !important;
  overflow: hidden !important;
}

.um-table-wrap .tabulator-row:hover { background: #F7F8FB !important; }
.um-table-wrap .tabulator-row.tabulator-row-even { background: #ffffff !important; }
.um-table-wrap .tabulator-row.tabulator-row-even:hover { background: #F7F8FB !important; }

.um-table-wrap .tabulator-cell {
  display: flex !important;
  align-items: center !important;
  flex-shrink: 0 !important;
  border-right: none !important;
  padding: 0 14px !important;
  color: #374151 !important;
  overflow: hidden !important;
  box-sizing: border-box !important;
}

.um-table-wrap .tabulator-tableHolder::-webkit-scrollbar { width: 4px; height: 4px; }
.um-table-wrap .tabulator-tableHolder::-webkit-scrollbar-track { background: transparent; }
.um-table-wrap .tabulator-tableHolder::-webkit-scrollbar-thumb {
  background: #E9E8E9;
  border-radius: 3px;
}

/* ── Cell helpers ── */
.um-addr-link {
  color: #343376;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}

html.dark .um-addr-link { color: #a5a8e8; }

.um-empty { color: #C9CCD2; }
.um-placeholder { padding: 48px; text-align: center; color: #9ca3af; font-size: 13px; }

.um-status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 99px;
  font-size: 10px;
  font-weight: 600;
}

.um-status-active { background: rgba(52, 51, 118, 0.08); color: #343376; }
.um-status-sold { background: rgba(252, 88, 104, 0.10); color: #D43847; }
.um-status-none { background: rgba(201, 204, 210, 0.20); color: #7E8B97; }

/* ── Tabulator dark mode ── */
html.dark .um-table-wrap .tabulator {
  background: #152030 !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}
html.dark .um-table-wrap .tabulator-header {
  background: #0E1B2C !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}
html.dark .um-table-wrap .tabulator-col {
  background: #0E1B2C !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}
html.dark .um-table-wrap .tabulator-col-title { color: #d6e4f0 !important; }
html.dark .um-table-wrap .tabulator-row {
  background: #152030 !important;
  border-color: rgba(255, 255, 255, 0.05) !important;
}
html.dark .um-table-wrap .tabulator-row:hover { background: #1d3048 !important; }
html.dark .um-table-wrap .tabulator-row.tabulator-row-even { background: #152030 !important; }
html.dark .um-table-wrap .tabulator-row.tabulator-row-even:hover { background: #1d3048 !important; }
html.dark .um-table-wrap .tabulator-cell {
  color: #d6e4f0 !important;
  border-color: rgba(255, 255, 255, 0.05) !important;
}
html.dark .um-table-wrap .tabulator-tableHolder { background: #152030 !important; }
html.dark .um-table-wrap .tabulator-tableHolder::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
}

html.dark .um-empty { color: rgba(255, 255, 255, 0.2) !important; }
html.dark .um-placeholder { color: rgba(214, 228, 240, 0.6); }

html.dark .um-status-active { background: rgba(165, 168, 232, 0.15); color: #a5a8e8; }
html.dark .um-status-sold { background: rgba(252, 88, 104, 0.15); color: #f87085; }
html.dark .um-status-none { background: rgba(255, 255, 255, 0.07); color: rgba(214, 228, 240, 0.5); }

/* ── Filter bar treeselect overrides ── */
.um-filter-bar .vue-treeselect__control {
  min-height: 32px !important;
  height: 32px !important;
  border: 1px solid #e8eaed !important;
  border-radius: 6px !important;
  background: #ffffff !important;
  font-size: 12px !important;
  font-family: Poppins, sans-serif !important;
}

.um-filter-bar .vue-treeselect__control:hover { border-color: #d1d5db !important; }
.um-filter-bar .vue-treeselect--focused .vue-treeselect__control {
  border-color: #12283F !important;
  box-shadow: none !important;
}

.um-filter-bar .vue-treeselect__placeholder,
.um-filter-bar .vue-treeselect__single-value {
  line-height: 30px !important;
  font-size: 12px !important;
  color: #9ca3af !important;
}

.um-filter-bar .vue-treeselect__multi-value { padding: 2px 4px !important; margin: 0 !important; }
.um-filter-bar .vue-treeselect__multi-value-item {
  background: #12283F !important;
  border: none !important;
  border-radius: 4px !important;
  color: #fff !important;
  font-size: 11px !important;
  padding: 1px 5px !important;
  margin: 1px 2px !important;
  height: 20px !important;
  line-height: 20px !important;
}

.um-filter-bar .vue-treeselect__value-remove {
  color: rgba(255, 255, 255, 0.6) !important;
  border-left-color: rgba(255, 255, 255, 0.2) !important;
}
.um-filter-bar .vue-treeselect__value-remove:hover {
  color: #fff !important;
  background: rgba(255, 255, 255, 0.15) !important;
}

.um-filter-bar .vue-treeselect__menu {
  font-size: 12px !important;
  border-radius: 8px !important;
  border: 1px solid #e8eaed !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1) !important;
}

.um-filter-bar .vue-treeselect__option--selected { background: #eff6ff !important; color: #1d4ed8 !important; }
.um-filter-bar .vue-treeselect__option:hover { background: #f9fafb !important; }
.um-filter-bar .vue-treeselect__label { font-size: 12px !important; color: #374151 !important; }

html.dark .um-filter-bar .vue-treeselect__control {
  background: #152030 !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}
html.dark .um-filter-bar .vue-treeselect__placeholder { color: rgba(255, 255, 255, 0.3) !important; }
html.dark .um-filter-bar .vue-treeselect__menu {
  background: #152030 !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}
html.dark .um-filter-bar .vue-treeselect__label { color: rgba(255, 255, 255, 0.8) !important; }
html.dark .um-filter-bar .vue-treeselect__option:hover { background: rgba(255, 255, 255, 0.07) !important; }
html.dark .um-filter-bar .vue-treeselect__multi-value-item { background: #343376 !important; }
</style>
